import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { getToken } from '../utils/auth';

const ProjectBillingPayments = ({ billingVuid, projectVuid, onPaymentAdded }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    receipt_number: '',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    reference: '',
    accounting_period_vuid: ''
  });
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [error, setError] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [billingTotal, setBillingTotal] = useState(0);
  const [amountError, setAmountError] = useState(null);
  const [postingPayment, setPostingPayment] = useState(null);

  useEffect(() => {
    if (billingVuid) {
      fetchPayments();
      fetchAccountingPeriods();
      fetchPaymentSummary();
      fetchBillingDetails();
    }
  }, [billingVuid]);

  const fetchBillingDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/project-billings/${billingVuid}`);
      setBillingTotal(parseFloat(response.data.total_amount) || 0);
    } catch (error) {
      console.error('Error fetching billing details:', error);
    }
  };

  const fetchPaymentSummary = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/project-billings/${billingVuid}/payment-summary`);
      setPaymentSummary(response.data);
    } catch (error) {
      console.error('Error fetching payment summary:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!billingVuid) {
        setError('Billing ID is required');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/project-billing-payments`, {
        params: { project_billing_vuid: billingVuid }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setPayments(response.data);
      } else {
        setPayments([]);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        request: error.request,
        config: error.config
      });
      
      let errorMessage = 'Failed to load payments';
      
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          errorMessage = 'Authentication required. Please log in again.';
        } else if (error.response.status === 404) {
          errorMessage = 'Payment receipts endpoint not found. Please contact support.';
        } else {
          errorMessage = error.response.data?.error || error.response.data?.message || `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        // Request made but no response
        console.error('No response received. API URL:', `${API_BASE_URL}/api/project-billing-payments`);
        errorMessage = `No response from server at ${API_BASE_URL}. Please check your connection and ensure the backend is running.`;
      } else {
        // Something else happened
        errorMessage = error.message || 'Failed to load payments';
      }
      
      setError(errorMessage);
      setPayments([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountingPeriods = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/accounting-periods`);
      setAccountingPeriods(response.data || []);
    } catch (error) {
      console.error('Error fetching accounting periods:', error);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setAmountError(null);

    // Validate amount doesn't exceed remaining balance
    const paymentAmount = parseFloat(formData.amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      setAmountError('Payment amount must be greater than 0');
      setCreating(false);
      return;
    }

    if (paymentSummary && paymentSummary.remaining_balance > 0) {
      if (paymentAmount > paymentSummary.remaining_balance) {
        setAmountError(`Payment amount (${formatCurrency(paymentAmount)}) exceeds remaining balance (${formatCurrency(paymentSummary.remaining_balance)}). Maximum allowed: ${formatCurrency(paymentSummary.remaining_balance)}`);
        setCreating(false);
        return;
      }
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/project-billing-payments`,
        {
          ...formData,
          project_billing_vuid: billingVuid,
          amount: paymentAmount
        }
      );

      setPayments([...payments, response.data]);
      
      // Show warning if payment exceeds invoice total
      if (response.data.warning) {
        alert(response.data.warning);
      }
      
      setShowCreateModal(false);
      setFormData({
        receipt_number: '',
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        reference: '',
        accounting_period_vuid: ''
      });
      
      // Refresh payment summary
      await fetchPaymentSummary();
      
      if (onPaymentAdded) {
        onPaymentAdded(response.data);
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      setError(error.response?.data?.error || 'Failed to create payment');
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePayment = async (paymentVuid) => {
    if (!window.confirm('Are you sure you want to void this payment?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/project-billing-payments/${paymentVuid}`);
      setPayments(payments.filter(p => p.vuid !== paymentVuid));
      await fetchPaymentSummary();
    } catch (error) {
      console.error('Error deleting payment:', error);
      setError(error.response?.data?.error || 'Failed to void payment');
    }
  };

  const handlePostPayment = async (paymentVuid) => {
    if (!window.confirm('Post this payment to the accounting period? This will create a journal entry.')) {
      return;
    }

    setPostingPayment(paymentVuid);
    setError(null);

    try {
      // Get the open accounting period
      const openPeriod = accountingPeriods.find(p => p.status === 'open');
      if (!openPeriod) {
        setError('No open accounting period found. Please open an accounting period first.');
        setPostingPayment(null);
        return;
      }

      // Post the payment receipt
      const response = await axios.post(`${API_BASE_URL}/api/posted-records/post`, {
        transaction_type: 'project_billing_payment',
        transaction_vuid: paymentVuid,
        posted_by: 'User' // Could get from auth context
      });

      if (response.data.success) {
        // Refresh payments to get updated posted status
        await fetchPayments();
        await fetchPaymentSummary();
        alert('Payment posted successfully!');
      } else {
        setError(response.data.error || 'Failed to post payment');
      }
    } catch (error) {
      console.error('Error posting payment:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to post payment';
      setError(errorMessage);
    } finally {
      setPostingPayment(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  if (loading) {
    return <div className="text-center py-4">Loading payments...</div>;
  }

  const totalPaid = payments
    .filter(p => p.status === 'active')
    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Payment Receipts</h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={async () => {
              // Refresh payments list
              await fetchPayments();
              await fetchPaymentSummary();
            }}
            className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-4 rounded-lg text-sm transition-colors"
            style={{ color: '#7F1D1D' }}
            data-tooltip="Refresh payments list"
          >
            Refresh
          </button>
          <button
            onClick={() => {
              // Set default amount to remaining balance when opening modal
              const defaultAmount = paymentSummary && paymentSummary.remaining_balance > 0 
                ? paymentSummary.remaining_balance.toFixed(2) 
                : '';
              setFormData({
                receipt_number: '',
                amount: defaultAmount,
                payment_date: new Date().toISOString().split('T')[0],
                reference: '',
                accounting_period_vuid: ''
              });
              setAmountError(null);
              setError(null);
              setShowCreateModal(true);
            }}
            className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-4 rounded-lg text-sm transition-colors"
            style={{ color: '#7F1D1D' }}
          >
            Add Receipt
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {paymentSummary && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-gray-500 mb-1">Billing Total</div>
              <div className="text-lg font-semibold text-gray-900">{formatCurrency(paymentSummary.billing_total)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Total Paid</div>
              <div className={`text-lg font-semibold ${paymentSummary.is_overpaid ? 'text-red-600' : 'text-gray-900'}`}>
                {formatCurrency(paymentSummary.total_paid)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Remaining Balance</div>
              <div className={`text-lg font-semibold ${
                paymentSummary.remaining_balance < 0 
                  ? 'text-red-600' 
                  : paymentSummary.remaining_balance > 0 
                  ? 'text-gray-900' 
                  : 'text-green-600'
              }`}>
                {formatCurrency(paymentSummary.remaining_balance)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Payment Count</div>
              <div className="text-lg font-semibold text-gray-900">{paymentSummary.payment_count}</div>
            </div>
          </div>
          {paymentSummary.is_overpaid && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              ⚠️ Total payments exceed billing amount
            </div>
          )}
          {paymentSummary.is_fully_paid && !paymentSummary.is_overpaid && (
            <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
              ✓ Billing fully paid
            </div>
          )}
        </div>
      )}

      {payments.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No payments recorded</p>
        </div>
      ) : (
        <>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipt Number
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Posted
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.vuid}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {payment.receipt_number}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(payment.payment_date)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {payment.reference || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        payment.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : payment.status === 'voided'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {payment.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {payment.exported_to_accounting ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          ✓ Posted
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                          Not Posted
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        {payment.status === 'active' && !payment.exported_to_accounting && (
                          <>
                            <button
                              onClick={() => handlePostPayment(payment.vuid)}
                              disabled={postingPayment === payment.vuid}
                              className={`px-3 py-1 text-xs rounded ${
                                postingPayment === payment.vuid
                                  ? 'bg-gray-400 text-white cursor-not-allowed'
                                  : 'bg-green-600 text-white hover:bg-green-700'
                              }`}
                            >
                              {postingPayment === payment.vuid ? 'Posting...' : 'Post'}
                            </button>
                            <button
                              onClick={() => handleDeletePayment(payment.vuid)}
                              className="text-red-600 hover:text-red-800 text-xs"
                            >
                              Void
                            </button>
                          </>
                        )}
                        {payment.status === 'active' && payment.exported_to_accounting && (
                          <span className="text-xs text-gray-500">Posted</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Create Payment Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Add Payment Receipt</h2>
            
            <form onSubmit={handleCreatePayment}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Receipt Number *
                </label>
                <input
                  type="text"
                  required
                  value={formData.receipt_number}
                  onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount *
                  {paymentSummary && paymentSummary.remaining_balance > 0 && (
                    <span className="text-xs text-gray-500 ml-2">
                      (Remaining: {formatCurrency(paymentSummary.remaining_balance)})
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  min="0"
                  max={paymentSummary && paymentSummary.remaining_balance > 0 ? paymentSummary.remaining_balance : undefined}
                  value={formData.amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, amount: value });
                    
                    // Clear error when user starts typing
                    if (amountError) {
                      setAmountError(null);
                    }
                    
                    // Validate in real-time
                    const numValue = parseFloat(value);
                    if (value && !isNaN(numValue) && numValue > 0) {
                      if (paymentSummary && paymentSummary.remaining_balance > 0 && numValue > paymentSummary.remaining_balance) {
                        setAmountError(`Amount exceeds remaining balance of ${formatCurrency(paymentSummary.remaining_balance)}`);
                      } else {
                        setAmountError(null);
                      }
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    amountError 
                      ? 'border-red-500 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-red-500'
                  }`}
                  placeholder={paymentSummary && paymentSummary.remaining_balance > 0 
                    ? `Default: ${formatCurrency(paymentSummary.remaining_balance)}` 
                    : '0.00'}
                />
                {amountError && (
                  <p className="mt-1 text-sm text-red-600">{amountError}</p>
                )}
                {paymentSummary && paymentSummary.remaining_balance <= 0 && (
                  <p className="mt-1 text-sm text-amber-600">
                    ⚠️ Billing is already fully paid. This payment will result in an overpayment.
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference
                </label>
                <input
                  type="text"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Accounting Period
                </label>
                <select
                  value={formData.accounting_period_vuid}
                  onChange={(e) => setFormData({ ...formData, accounting_period_vuid: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Select Period</option>
                  {accountingPeriods.map((period) => {
                    const monthNames = [
                      'January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'
                    ];
                    const monthName = monthNames[period.month - 1] || period.month;
                    const displayText = period.description 
                      ? `${period.description} (${monthName} ${period.year})`
                      : `${monthName} ${period.year}`;
                    return (
                      <option key={period.vuid} value={period.vuid}>
                        {displayText} {period.status === 'closed' ? '(Closed)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setError(null);
                    setAmountError(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !!amountError}
                  className={`px-4 py-2 font-semibold rounded-md text-white transition-colors ${
                    creating || amountError
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  style={!creating && !amountError ? { backgroundColor: '#7F1D1D', color: 'white' } : {}}
                >
                  {creating ? 'Creating...' : 'Create Receipt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectBillingPayments;

